import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const ENDPOINT = process.env.REACT_APP_API_ENDPOINT || "https://s902oia0ti.execute-api.us-east-1.amazonaws.com/";

export default function SearchBar() {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const resultsRef = React.useRef();
  const inputRef = React.useRef();

  useEffect(() => {
    if (!searchTerm) {
      setSearchResults([]);
      setSearchError(null);
      return;
    }
    const timeout = setTimeout(() => {
      setSearchLoading(true);
      setSearchError(null);
      fetch(`${ENDPOINT}/search/?query=${encodeURIComponent(searchTerm)}`)
        .then(async (res) => {
          const contentType = res.headers.get("content-type");
          if (!contentType || !contentType.includes("application/json")) {
            throw new Error("Respuesta no es JSON");
          }
          return res.json();
        })
        .then((data) => {
          // Permitir tanto array directo como objeto con perfumes
          let perfumes = [];
          if (Array.isArray(data)) {
            perfumes = data;
          } else if (Array.isArray(data.perfumes)) {
            perfumes = data.perfumes;
          }
          setSearchResults(perfumes.slice(0, 5));
        })
        .catch((err) => {
          setSearchError(err.message || "Error al buscar artículos");
          setSearchResults([]);
        })
        .finally(() => setSearchLoading(false));
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  // Manejo de teclado para navegación
  useEffect(() => {
    if (!searchTerm || searchResults.length === 0) {
      setHighlightedIndex(-1);
      return;
    }
    setHighlightedIndex(0);
  }, [searchTerm, searchResults.length]);

  // Limpiar búsqueda al perder foco
  useEffect(() => {
    const handleBlur = (e) => {
      // Si el blur es hacia un elemento dentro del dropdown, no limpiar
      if (e.relatedTarget && e.relatedTarget.closest && e.relatedTarget.closest('.searchbar-dropdown')) {
        return;
      }
      setSearchTerm("");
    };
    const input = inputRef.current;
    if (input) {
      input.addEventListener('blur', handleBlur);
    }
    return () => {
      if (input) {
        input.removeEventListener('blur', handleBlur);
      }
    };
  }, []);

  const handleKeyDown = (e) => {
    if (!searchResults.length && e.key !== "Escape") return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % searchResults.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev - 1 + searchResults.length) % searchResults.length);
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      const item = searchResults[highlightedIndex];
      if (item) {
        window.location.href = `/${item.sku || item.id}`;
        setSearchTerm("");
      }
    } else if (e.key === "Escape") {
      setSearchTerm("");
    }
  };

  return (
    <div className="flex flex-col items-center mb-8 w-full">
      <input
        ref={inputRef}
        type="text"
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        placeholder="Buscar aromas..."
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-lg"
        style={{margin: "0 auto"}}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      />
      {searchTerm && (
        <div className="w-full bg-white border border-gray-200 rounded-b-lg shadow-md mt-1 z-10 searchbar-dropdown">
          {searchLoading && <div className="p-2 text-gray-500">Buscando...</div>}
          {searchError && <div className="p-2 text-red-500">{searchError}</div>}
          {!searchLoading && !searchError && searchResults.length === 0 && (
            <div className="p-2 text-gray-500">Sin resultados</div>
          )}
          {!searchLoading && !searchError && searchResults.map((item, idx) => {
            const imageRepository = "https://s3.us-east-1.amazonaws.com/esencianomada.com.ar";
            const imageUrl = `${imageRepository}/${item.images['thumbnail']}`;
            const isActive = idx === highlightedIndex;
            const imgWidth = 48; // px (w-12)
            const imgHeight = 48; // px (h-12)
            return (
              <Link
                key={item.sku || item.id}
                to={`/${item.sku || item.id}`}
                className={`flex items-start gap-3 px-4 py-2 border-b last:border-b-0 border-gray-100 text-gray-800 cursor-pointer hover:bg-blue-50 ${isActive ? 'bg-blue-100' : ''}`}
                onClick={() => setSearchTerm("")}
                tabIndex={-1}
                ref={isActive ? resultsRef : null}
              >
                <img
                  src={imageUrl}
                  alt={item.name}
                  width={imgWidth}
                  height={imgHeight}
                  className="w-12 h-12 object-contain rounded bg-white border border-gray-200 flex-shrink-0"
                  loading="lazy"
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate text-base">{item.name}</div>
                  <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                    {item.description}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
